/* eslint-disable jsdoc/require-file-overview -- When touching please add documentation. */
import { Maybe, NonEmptyList } from 'purify-ts';
import { AssertionError } from './assert';
import { DefaultMap } from './defaultdict';
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* islice(iter, start, end) {
    let i = 0;
    for (const el of iter) {
        if (end != null && i >= end) {
            break;
        }
        i++;
        if (i < start) {
            continue;
        }
        yield el;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* chain(iterables) {
    for (const iterable of iterables) {
        yield* iterable;
    }
}
/**
 * A wrapper around `chain` to be used easily with arrays.
 *
 * @param arrays The arrays you want to join into a single iterable.
 * @returns The chained arrays.
 */
export function chainArrays(...arrays) {
    return chain(arrays);
}
/**
 * The same as {@link Array.some} but for iterables.
 *
 * @param iter The iterable to check.
 * @param cond The condition to check with.
 * @returns If `cond` returned `true` at least one time.
 */
export function some(iter, cond) {
    let i = 0;
    for (const el of iter) {
        if (cond(el, i)) {
            return true;
        }
        i += 1;
    }
    return false;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function all(iter, cond) {
    return !some(iter, (el, idx) => !cond(el, idx));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* map(arr, mapper) {
    let i = 0;
    for (const el of arr) {
        yield mapper(el, i);
        i++;
    }
}
/**
 * Map function that returns an iterable over a iterable returning a
 * non nested iterable.
 *
 * Only use this function if it makes sense to get an iterable,
 * otherwise just use flatMap1 from this module.
 *
 * @param arr The iterable to map.
 * @param mapper The mapper that will be applied for each element in
 * arr.
 * @yields An iterable containing each element yielded by mapper.
 */
export function* flatMap1Iter(arr, mapper) {
    let i = 0;
    for (const el of arr) {
        yield* mapper(el, i);
        i++;
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function flatMap1(arr, mapper) {
    const res = [];
    let i = 0;
    for (const el of arr) {
        for (const subEl of mapper(el, i)) {
            res.push(subEl);
        }
        i++;
    }
    return res;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* flat1(arr) {
    for (const subArr of arr) {
        for (const el of subArr) {
            yield el;
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function reduce(it, reducer, initial) {
    let index = 0;
    let res = initial;
    for (const el of it) {
        res = reducer(res, el, index);
        index += 1;
    }
    return res;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function first(it) {
    // eslint-disable-next-line no-unreachable-loop
    for (const el of it) {
        return Maybe.of(el);
    }
    return Maybe.empty();
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function groupBy(arr, mapper) {
    return reduce(arr, (acc, el, index) => {
        const key = mapper(el, index);
        acc.get(key).push(el);
        return acc;
    }, new DefaultMap((_) => []));
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* filter(filterFunc, arr) {
    let i = 0;
    for (const el of arr) {
        const val = filterFunc(el, i++);
        if (val.isJust()) {
            yield val.extract();
        }
    }
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export function* unique(getKey, arr) {
    const seen = new Set();
    for (const item of arr) {
        const key = getKey(item);
        if (!seen.has(key)) {
            seen.add(key);
            yield item;
        }
    }
}
/**
 * Batch an iterable into chunks of a specific size, both in amount of items per batch and an
 * arbitrary "size" of a batch.
 *
 * @param input The iterable that should be batched.
 * @param props The way to do the batching, see {@link BatchProps} for more information.
 * @yields Not empty arrays that contain items from `input` (in the same order as `input`) but is
 * chunked into batches.
 */
export function* sizedBatch(input, props) {
    let cur = [];
    let curSize = 0;
    for (const el of input) {
        const newSize = props.getSize(el);
        if (NonEmptyList.isNonEmpty(cur) &&
            (cur.length === props.maxBatchAmount || newSize + curSize > props.maxBatchSize)) {
            yield cur;
            curSize = 0;
            cur = [];
        }
        cur.push(el);
        curSize += newSize;
    }
    if (NonEmptyList.isNonEmpty(cur)) {
        yield cur;
    }
}
/**
 * Batch an iterable into batches of size `size`.
 *
 * This is an optimized version of {@link sizedBatch}, which only works for batches of a single
 * size.
 *
 * @param input The iterable that should be batched.
 * @param size The maximum size a batch should have, the last batch might have a smaller size.
 * @yields Not empty arrays that contain at most `size` amount of elements.
 */
export function* batch(input, size) {
    AssertionError.assert(size > 0, () => `The size should larger than 0, it was ${size}`);
    let cur = [];
    for (const el of input) {
        if (NonEmptyList.isNonEmpty(cur) && cur.length === size) {
            yield cur;
            cur = [];
        }
        cur.push(el);
    }
    if (NonEmptyList.isNonEmpty(cur)) {
        yield cur;
    }
}
